import { motion } from "framer-motion";
import { Activity, TrendingUp, Zap } from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1762279389020-eeeb69c25813?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMHRyYWRpbmclMjBkYXRhfGVufDB8fHx8MTc4MzkyODg0OHww&ixlib=rb-4.1.0&q=85";

export default function Hero({ scannedCount = 0, lastRefreshed, onScroll, hits = 0 }) {
  return (
    <section className="relative overflow-hidden pt-20 pb-24 lg:pt-28 lg:pb-32" data-testid="hero-section">
      {/* orbs */}
      <div className="hero-orb bg-[#00E5FF]/40 -top-40 -left-32" />
      <div className="hero-orb bg-[#39FF14]/25 top-40 right-[-140px]" />

      {/* Bg image */}
      <div
        className="absolute inset-0 -z-10 opacity-[0.18] pointer-events-none"
        style={{
          backgroundImage: `url("${HERO_BG}")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-transparent via-[#0A0A0A]/70 to-[#0A0A0A]" />

      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-2"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-[#00E5FF]/25 bg-[#00E5FF]/8 px-3 py-1 text-xs font-mono tracking-widest text-[#00E5FF]">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#00E5FF] opacity-60" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#00E5FF]" />
            </span>
            LIVE · NSE EQUITY UNIVERSE · {scannedCount || 750} STOCKS
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05, duration: 0.7 }}
          className="mt-6 font-display text-4xl sm:text-5xl lg:text-7xl font-semibold leading-[1.02] max-w-4xl"
        >
          Psychological levels,{" "}
          <span className="text-[#00E5FF]">quantified.</span>
          <br />
          <span className="text-white/60">Backtested on NSE equity.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mt-6 max-w-2xl text-base sm:text-lg text-white/60"
        >
          A scanner that tracks ~750 NSE equity stocks (Nifty Total Market) closing their <span className="text-white">1-hour candle</span> above
          round-number levels of ₹100, 200, 500, 1,000, 5,000 and 10,000 &mdash; then measures how those breakouts
          performed over the following <span className="text-white">5 trading days</span> with the candle&apos;s low as stop-loss.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.5 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-scan-cta"
            onClick={onScroll}
            className="pill-btn-primary inline-flex items-center gap-2"
          >
            <Zap className="h-4 w-4" strokeWidth={2.4} />
            View Live Scanner
          </button>
          <a
            href="#backtest"
            data-testid="hero-backtest-cta"
            className="pill-btn inline-flex items-center gap-2 text-white/85"
          >
            <TrendingUp className="h-4 w-4" strokeWidth={2} />
            See Backtest Report
          </a>
          {lastRefreshed && (
            <span className="ml-2 font-mono text-xs text-white/45">
              Last refreshed · {new Date(lastRefreshed).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST
            </span>
          )}
        </motion.div>

        {/* mini stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          <MiniStat label="Universe" value={scannedCount || 60} suffix="stocks" icon={<Activity className="h-3.5 w-3.5" />} />
          <MiniStat label="Live signals" value={hits} suffix="hits" tone="cyan" />
          <MiniStat label="Levels tracked" value={6} suffix="rounds" />
          <MiniStat label="Timeframe" value={"1H"} suffix="candles" />
        </motion.div>
      </div>
    </section>
  );
}

function MiniStat({ label, value, suffix, tone, icon }) {
  return (
    <div className="glass rounded-xl px-5 py-4">
      <div className="flex items-center gap-2 text-[10px] font-mono tracking-widest text-white/45 uppercase">
        {icon}
        {label}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className={`font-display text-3xl font-semibold ${tone === "cyan" ? "text-[#00E5FF]" : "text-white"}`}>
          {value}
        </span>
        <span className="font-mono text-xs text-white/40">{suffix}</span>
      </div>
    </div>
  );
}
